import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed | error
  const [tx, setTx] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async (count) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus(data.payment_status);
          return;
        }
        if (count + 1 >= MAX_POLLS) {
          setStatus("error");
          return;
        }
        setAttempts(count + 1);
        timer = setTimeout(() => poll(count + 1), POLL_INTERVAL);
      } catch {
        setStatus("error");
      }
    };
    poll(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, refresh]);

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6"
      data-testid="payment-success-page"
    >
      <div className="w-full max-w-md text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-6 text-white/70" />
            <div className="label-eyebrow mb-3">confirming payment</div>
            <h1 className="font-display text-3xl font-black tracking-tight mb-3">
              Verifying with Polar…
            </h1>
            <p className="text-white/60 text-[14px]" data-testid="payment-status-checking">
              Attempt {attempts + 1} / {MAX_POLLS}. This usually takes a couple of seconds.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-6 text-[#10A37F]" />
            <div className="label-eyebrow mb-3">payment received</div>
            <h1 className="font-display text-4xl font-black tracking-tighter mb-3">
              +{tx?.credits ?? 0} coins
            </h1>
            <p className="text-white/60 text-[14px] mb-10" data-testid="payment-status-paid">
              Your balance has been updated. You can now run more comparisons.
            </p>
            <button
              onClick={() => nav("/")}
              className="btn-pill focus-ring"
              data-testid="return-to-app"
            >
              <Sparkles className="w-4 h-4" />
              Continue searching
            </button>
          </>
        )}
        {(status === "expired" || status === "failed" || status === "error") && (
          <>
            <XCircle className="w-10 h-10 mx-auto mb-6 text-[#ff9c85]" />
            <div className="label-eyebrow mb-3">payment {status}</div>
            <h1 className="font-display text-3xl font-black tracking-tight mb-3">
              {status === "error"
                ? "Couldn't confirm the payment"
                : status === "expired"
                ? "Session expired"
                : "Payment failed"}
            </h1>
            <p className="text-white/60 text-[14px] mb-10" data-testid={`payment-status-${status}`}>
              No charge was applied. You can try again or head back to the app.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => nav("/pricing")} className="btn-pill focus-ring" data-testid="try-again-btn">
                Try again
              </button>
              <button onClick={() => nav("/")} className="btn-ghost focus-ring">
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
